import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Button, ModalWrapper, SearchableDropdown } from "../../../components/ui";
import { X } from "../../../components/icons/svg/outline";
import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { createWorkScope, getUnits } from "../../../services/api/dispatch";
import { sortAlphabetically } from "../../../utils/collections/sort";
import { formatDate } from "../../../utils/datetime";

const TITLE_ID = "work-scope-modal-title";
const FIELD_CLASS =
  "mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100";
const CLOSE_BUTTON_CLASS =
  "inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30";

/**
 * Read the first validation message from a failed work-scope create response.
 *
 * @param {unknown} error Axios-style error from `createWorkScope`.
 * @returns {string}
 */
function readCreateError(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "The work scope could not be created. Please try again.";
  }
  if (typeof data.detail === "string" && data.detail) {
    return data.detail;
  }
  const first = Object.values(data).flat()[0];
  if (typeof first === "string" && first) {
    return first;
  }
  return "The work scope could not be created. Please try again.";
}

/**
 * Shared header for the assign and create steps.
 *
 * @param {Object} props
 * @param {string} props.title Heading text.
 * @param {string} [props.subtitle] Job and date line under the heading.
 * @param {Function} props.onClose Header close action.
 * @returns {JSX.Element}
 */
function WorkScopeModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="bg-primary px-5 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <h2
          id={TITLE_ID}
          className="min-w-0 text-lg font-semibold leading-none tracking-tight text-white"
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className={CLOSE_BUTTON_CLASS}
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {subtitle ? (
        <p className="mt-1 text-sm leading-snug text-white/80">{subtitle}</p>
      ) : null}
    </div>
  );
}

/**
 * Add or edit the catalog work scope and quantity on a dispatch job/day.
 *
 * `mode="add"` starts empty. `mode="edit"` prefills `initialScope` and
 * `initialQuantity`. When the scope search has no matches, the same modal
 * switches to a create step that saves a persistent WorkScope and then
 * returns here with that scope selected.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {"add"|"edit"} props.mode Add a scope to the day, or edit the current one.
 * @param {Function} props.onConfirm `(scope, quantity) => void` when the assignment is confirmed.
 * @param {Function} props.onClose Close without applying a selection.
 * @param {Object} [props.job=null] Job shown in the header.
 * @param {Date} [props.date=null] Dispatch date shown in the header.
 * @param {Object} [props.initialScope=null] Catalog scope pre-selected in edit mode.
 * @param {number|string} [props.initialQuantity=null] Quantity pre-filled in edit mode.
 * @returns {JSX.Element|null}
 */
const WorkScopeModal = ({
  isOpen,
  mode,
  onConfirm,
  onClose,
  job = null,
  date = null,
  initialScope = null,
  initialQuantity = null,
}) => {
  const { scopeObjects, addScopeObject } = useContext(DispatchContext);
  const [step, setStep] = useState("assign");
  const [selectedScope, setSelectedScope] = useState("");
  const [quantity, setQuantity] = useState("");
  const [createName, setCreateName] = useState("");
  const [unitId, setUnitId] = useState(null);
  const [units, setUnits] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!isOpen) {
      setStep("assign");
      return;
    }
    if (!justOpened) {
      return;
    }
    setStep("assign");
    setSelectedScope(mode === "edit" ? initialScope?.id ?? "" : "");
    setQuantity(mode === "edit" ? initialQuantity ?? "" : "");
    setCreateName("");
    setUnitId(null);
    setError("");
    setIsSaving(false);
    savingRef.current = false;
  }, [isOpen, mode, initialScope, initialQuantity]);

  useEffect(() => {
    if (!isOpen || step !== "create") {
      return undefined;
    }
    let cancelled = false;
    setIsLoadingUnits(true);
    getUnits()
      .then((rows) => {
        if (!cancelled) {
          setUnits(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUnits([]);
          setError("Units could not be loaded. You can still save without a unit.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingUnits(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, step]);

  const scopeOptions = useMemo(
    () =>
      (scopeObjects ?? []).map((scopeObject) => ({
        value: scopeObject.id,
        label: scopeObject.title,
      })),
    [scopeObjects]
  );

  const unitOptions = useMemo(
    () =>
      sortAlphabetically(
        units.map((unit) => ({
          value: unit.id,
          label: unit.name,
        })),
        "label"
      ),
    [units]
  );

  if (!isOpen) {
    return null;
  }

  const isEdit = mode === "edit";
  const jobLabel = job ? `${job.number} - ${job.description}` : "";
  const dateLabel = date ? formatDate(date, "weekday MM/DD/YY") : "";
  const contextLabel = [jobLabel, dateLabel].filter(Boolean).join(" · ");
  const canAssign = selectedScope !== "" && selectedScope != null;
  const canCreate = createName.trim().length > 0 && !isSaving;

  /**
   * Store the catalog scope chosen in the searchable dropdown.
   *
   * @param {string|number|null} value Selected scope id.
   * @returns {void}
   */
  const handleSelectChange = (value) => {
    setSelectedScope(value ?? "");
  };

  /**
   * Store the quantity typed for the selected scope.
   *
   * @param {import("react").ChangeEvent<HTMLInputElement>} event Input change event.
   * @returns {void}
   */
  const handleQuantityChange = (event) => {
    setQuantity(event.target.value);
  };

  /**
   * Switch to the create step, prefilling the name from the unmatched search.
   *
   * @param {string} searchTerm Text typed into the scope dropdown.
   * @returns {void}
   */
  const handleEmptyScopeList = (searchTerm) => {
    setCreateName(String(searchTerm || "").trim());
    setUnitId(null);
    setError("");
    setIsSaving(false);
    savingRef.current = false;
    setStep("create");
  };

  /**
   * Leave the create step and return to scope selection.
   *
   * @returns {void}
   */
  const handleBackToAssign = () => {
    setError("");
    setStep("assign");
  };

  /**
   * Restore the opened values and close without applying.
   *
   * @returns {void}
   */
  const handleCancel = () => {
    setSelectedScope(isEdit ? initialScope?.id ?? "" : "");
    setQuantity(isEdit ? initialQuantity ?? "" : "");
    setStep("assign");
    onClose();
  };

  /**
   * Header close: leave the create step, or dismiss the modal from the assign step.
   *
   * @returns {void}
   */
  const handleHeaderClose = () => {
    if (step === "create") {
      handleBackToAssign();
      return;
    }
    handleCancel();
  };

  /**
   * Save a new catalog work scope and select it on the assign step.
   *
   * @returns {Promise<void>}
   */
  const handleCreateConfirm = async () => {
    const title = createName.trim();
    if (!title || savingRef.current) {
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    setError("");
    try {
      const created = await createWorkScope({
        title,
        unit: unitId || null,
      });
      addScopeObject?.(created);
      setSelectedScope(created?.id ?? "");
      setStep("assign");
    } catch (saveError) {
      savingRef.current = false;
      setError(readCreateError(saveError));
      setIsSaving(false);
    }
  };

  /**
   * Return the selected catalog scope and quantity to the parent.
   *
   * @returns {void}
   */
  const handleAssignConfirm = () => {
    if (!canAssign) {
      return;
    }
    const scopeObject = (scopeObjects ?? []).find(
      (scope) => String(scope.id) === String(selectedScope)
    );
    if (!scopeObject) {
      return;
    }
    setSelectedScope("");
    setQuantity("");
    onConfirm(scopeObject, quantity);
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleHeaderClose}
      closeButton={false}
      maxWidth={560}
      className="relative w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-0 shadow-xl"
      outsideClickIgnoreSelector="[data-searchable-dropdown-listbox]"
    >
      <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        {step === "create" ? (
          <>
            <WorkScopeModalHeader
              title="Create New Work Scope"
              onClose={handleHeaderClose}
            />
            <div className="space-y-4 px-5 py-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                This will add a new persistent Work Scope to the database. Please be
                sure an existing work scope does not already exist for this.
              </div>

              <label
                className="block text-sm font-semibold text-gray-800"
                htmlFor="work-scope-name"
              >
                Name
                <input
                  id="work-scope-name"
                  type="text"
                  value={createName}
                  maxLength={1000}
                  autoFocus
                  onChange={(event) => setCreateName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCreateConfirm();
                    }
                  }}
                  className={FIELD_CLASS}
                />
              </label>

              <div>
                <span className="block text-sm font-semibold text-gray-800">Unit</span>
                <SearchableDropdown
                  options={unitOptions}
                  value={unitId}
                  onChange={setUnitId}
                  placeholder={isLoadingUnits ? "Loading units..." : "Select a unit"}
                  ariaLabel="Work scope unit"
                  disabled={isLoadingUnits}
                  searchWhenOverflow
                  className="mt-1.5"
                  listboxClassName="z-[10060]"
                />
              </div>

              {error ? (
                <p className="text-sm text-red-700" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="flex gap-3">
                <Button
                  variant="cancel"
                  text="Cancel"
                  onClick={handleBackToAssign}
                  disabled={isSaving}
                  className="flex-1 rounded-lg"
                />
                <Button
                  variant={canCreate ? "secondary" : "disabled"}
                  text={isSaving ? "Saving..." : "Confirm"}
                  onClick={handleCreateConfirm}
                  disabled={!canCreate}
                  className="flex-1 rounded-lg"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <WorkScopeModalHeader
              title={isEdit ? "Change Scope" : "Add Scope"}
              subtitle={contextLabel}
              onClose={handleHeaderClose}
            />
            <div className="space-y-4 px-5 py-4">
              <div>
                <span className="mb-1.5 block text-sm font-semibold text-gray-800">
                  Scope
                </span>
                <SearchableDropdown
                  options={scopeOptions}
                  value={selectedScope || null}
                  onChange={handleSelectChange}
                  placeholder="Select a scope"
                  ariaLabel="Select a scope"
                  listboxClassName="z-[10060]"
                  emptyListAction={
                    Array.isArray(scopeObjects)
                      ? {
                          label: "< Add New >",
                          onSelect: handleEmptyScopeList,
                        }
                      : null
                  }
                />
              </div>

              <label
                className="block text-sm font-semibold text-gray-800"
                htmlFor="work-scope-quantity"
              >
                Quantity
                <input
                  id="work-scope-quantity"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  value={quantity}
                  placeholder="Quantity"
                  onChange={handleQuantityChange}
                  min="1"
                  className={FIELD_CLASS}
                />
              </label>

              <Button
                onClick={handleAssignConfirm}
                text="Confirm"
                variant={canAssign ? "secondary" : "disabled"}
                disabled={!canAssign}
                className="w-full rounded-lg"
              />
            </div>
          </>
        )}
      </div>
    </ModalWrapper>
  );
};

export default WorkScopeModal;
